import { CustodianActivityStates } from '@generated/graphql';
import { CustodianActivityTransactionTypes } from '@interfaces/custodianActivity';
import { usePageState } from '@utils/usePageState';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { filtersBaseType } from '../filters/activitiesFiltersCard.types';
import {
  ACTIVITIES_DEFAULT_FILTER_VALUES,
  hasActivitiesActiveFilters,
} from '../utils/activitiesFilterDefaults';
import { buildActivitiesGridFilter } from '../utils/activitiesGridVariables';

const SEARCH_DEBOUNCE_MS = 300;

const TRANSACTION_TYPE_OPTIONS = Object.values(
  CustodianActivityTransactionTypes,
);

const ACTIVITY_STATES: readonly string[] = Object.values(
  CustodianActivityStates,
);

const isCustodianActivityState = (
  value: string,
): value is CustodianActivityStates => ACTIVITY_STATES.includes(value);

type UseActivitiesFiltersInput = {
  orgId: string | undefined;
  setPage: (page: number) => void;
};

export const useActivitiesFilters = ({
  orgId,
  setPage,
}: UseActivitiesFiltersInput): {
  gridFilter: ReturnType<typeof buildActivitiesGridFilter>;
  state: CustodianActivityStates;
  filtersBaseProps: filtersBaseType;
} => {
  const [stateStored, setStateStored] = usePageState(
    ACTIVITIES_DEFAULT_FILTER_VALUES.state,
    'raState',
  );
  const state = isCustodianActivityState(stateStored)
    ? stateStored
    : ACTIVITIES_DEFAULT_FILTER_VALUES.state;

  const [transactionType, setTransactionType] = usePageState(
    ACTIVITIES_DEFAULT_FILTER_VALUES.transactionType,
    'raType',
  );
  const [agingFilter, setAgingFilter] = usePageState(
    ACTIVITIES_DEFAULT_FILTER_VALUES.agingFilter,
    'raAge',
  );
  const [searchStored, setSearchStored] = usePageState(
    ACTIVITIES_DEFAULT_FILTER_VALUES.search,
    'raSearch',
  );
  const [searchDraft, setSearchDraft] = useState(searchStored);

  useEffect(() => {
    setSearchDraft(searchStored);
  }, [searchStored]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchDraft === searchStored) {
        return;
      }
      setSearchStored(searchDraft);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchDraft, searchStored, setPage, setSearchStored]);

  const resetPage = useCallback(() => setPage(1), [setPage]);

  const gridFilter = useMemo(
    () =>
      buildActivitiesGridFilter({
        orgId,
        state,
        transactionType,
        agingFilter,
        search: searchStored,
      }),
    [agingFilter, orgId, searchStored, state, transactionType],
  );

  const filterValues = useMemo(
    () => ({
      state,
      transactionType,
      agingFilter,
      search: searchStored,
    }),
    [agingFilter, searchStored, state, transactionType],
  );

  const filtersBaseProps = {
    state,
    onStateChange: (next: string) => {
      setStateStored(next);
      resetPage();
    },
    transactionType,
    onTransactionTypeChange: (next: string) => {
      setTransactionType(next);
      resetPage();
    },
    transactionTypeOptions: TRANSACTION_TYPE_OPTIONS,
    agingFilter,
    onAgingFilterChange: (next: string) => {
      setAgingFilter(next);
      resetPage();
    },
    search: searchDraft,
    onSearchChange: (event: ChangeEvent<HTMLInputElement>) => {
      setSearchDraft(event.target.value);
    },
    hasActiveFilters: hasActivitiesActiveFilters(filterValues),
    onClearFilters: () => {
      setStateStored(ACTIVITIES_DEFAULT_FILTER_VALUES.state);
      setTransactionType(ACTIVITIES_DEFAULT_FILTER_VALUES.transactionType);
      setAgingFilter(ACTIVITIES_DEFAULT_FILTER_VALUES.agingFilter);
      setSearchStored(ACTIVITIES_DEFAULT_FILTER_VALUES.search);
      setSearchDraft(ACTIVITIES_DEFAULT_FILTER_VALUES.search);
      resetPage();
    },
  };

  return { gridFilter, state, filtersBaseProps };
};
